import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, ButtonGroup, Heading, Select, Spinner, Stack, Table, Td, Text, TextLink, Th, Tr,
} from '@ui'
import { Link, useSearchParams } from 'react-router-dom'
import { getJSON } from '../api.js'
import { MediaIcons } from '../components/Photos.jsx'
import { StarIcon } from '../components/Star.jsx'
import { addDays, dateKey, todayKey, utcDate } from '../dates.js'
import {
  formatDistance, formatDuration, formatNumber, formatPace, formatTime, sourceLabel, ukDateKey, UNITS,
} from '../format.js'
import { useStoredState } from '../storage.js'
import { useUnit } from '../units.jsx'

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// Calendar dates are handled as plain (year, month, day) in UTC so no time zone or clock change can
// shift a day; walks are placed by their UK date (ukDateKey).
const mondayOf = (d) => addDays(d, -((d.getUTCDay() + 6) % 7))

// The weeks (Monday first) covering a month, each a list of 7 UTC dates.
function weeksOf(y, m) {
  const first = new Date(Date.UTC(y, m, 1))
  const weeks = []
  for (let d = mondayOf(first); d.getUTCMonth() === m || d < first; ) {
    const week = []
    for (let i = 0; i < 7; i++) { week.push(d); d = addDays(d, 1) }
    weeks.push(week)
  }
  return weeks
}

// Which period the URL asks for: ?view=month&month=YYYY-MM (the default), ?view=week&week=<a
// date in it>, ?view=year&year=YYYY. Missing or malformed values mean "the one containing today".
function periodFromParams(params) {
  const view = ['week', 'year'].includes(params.get('view')) ? params.get('view') : 'month'
  const today = utcDate(todayKey())
  if (view === 'week') {
    const w = params.get('week')
    return { view, monday: mondayOf(/^\d{4}-\d{2}-\d{2}$/.test(w ?? '') ? utcDate(w) : today) }
  }
  if (view === 'year') {
    const y = Number(params.get('year'))
    return { view, y: Number.isInteger(y) && y > 1900 ? y : today.getUTCFullYear() }
  }
  const match = /^(\d{4})-(\d{2})$/.exec(params.get('month') ?? '')
  return match
    ? { view, y: Number(match[1]), m: Number(match[2]) - 1 }
    : { view, y: today.getUTCFullYear(), m: today.getUTCMonth() }
}

const monthParams = (y, m) => {
  const d = new Date(Date.UTC(y, m, 1))
  return { view: 'month', month: `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}` }
}
const weekParams = (d) => ({ view: 'week', week: dateKey(mondayOf(d)) })
const yearParams = (y) => ({ view: 'year', year: String(y) })
const toSearch = (p) => `?${new URLSearchParams(p)}`

const counted = (list) => list.filter((w) => w.is_counted)
const sumMeters = (list) => counted(list).reduce((s, w) => s + w.distance_meters, 0)
const walksLabel = (n) => `${n} ${n === 1 ? 'walk' : 'walks'}`

// Calendar: month, week and year views of the walks, totals counting each walk once (as the walks
// table and monthly totals do). "Every recording" also shows the duplicates, greyed.
export default function Calendar() {
  const [unit] = useUnit()
  const [params, setParams] = useSearchParams()
  const [walks, setWalks] = useState(null)
  const [error, setError] = useState(null)
  const [show, setShow] = useStoredState('walks.show', 'counted')
  const period = periodFromParams(params)

  useEffect(() => {
    const controller = new AbortController()
    getJSON('/api/walks', { signal: controller.signal })
      .then(setWalks)
      .catch((err) => { if (err.name !== 'AbortError') setError(err.message) })
    return () => controller.abort()
  }, [])

  const byDay = useMemo(() => {
    const days = new Map()
    for (const w of walks ?? []) {
      if (show !== 'all' && !w.is_counted) continue
      const key = ukDateKey(w.start_time)
      if (!days.has(key)) days.set(key, [])
      days.get(key).push(w)
    }
    for (const list of days.values()) list.sort((a, b) => (a.start_time < b.start_time ? -1 : 1))
    return days
  }, [walks, show])

  if (error) return <Alert variant="danger">Couldn't load walks: {error}</Alert>
  if (!walks) return <Spinner />

  // Switching view keeps the date in focus: today if it's in the current period, else its start.
  const today = utcDate(todayKey())
  let focus, title, prev, next, current
  if (period.view === 'month') {
    const { y, m } = period
    focus = today.getUTCFullYear() === y && today.getUTCMonth() === m ? today : new Date(Date.UTC(y, m, 1))
    title = new Date(Date.UTC(y, m, 1)).toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' })
    prev = monthParams(y, m - 1)
    next = monthParams(y, m + 1)
    current = { view: 'month' }
  } else if (period.view === 'week') {
    const { monday } = period
    const sunday = addDays(monday, 6)
    focus = today >= monday && today <= sunday ? today : monday
    const fmt = (d, opts) => d.toLocaleDateString('en-GB', { timeZone: 'UTC', ...opts })
    title = `Week of ${fmt(monday, { day: 'numeric', month: 'short' })} – ${fmt(sunday, { day: 'numeric', month: 'short', year: 'numeric' })}`
    prev = weekParams(addDays(monday, -7))
    next = weekParams(addDays(monday, 7))
    current = { view: 'week' }
  } else {
    const { y } = period
    focus = today.getUTCFullYear() === y ? today : new Date(Date.UTC(y, 0, 1))
    title = String(y)
    prev = yearParams(y - 1)
    next = yearParams(y + 1)
    current = { view: 'year' }
  }
  const views = {
    month: monthParams(focus.getUTCFullYear(), focus.getUTCMonth()),
    week: weekParams(focus),
    year: yearParams(focus.getUTCFullYear()),
  }
  const thisLabel = { month: 'This month', week: 'This week', year: 'This year' }[period.view]

  return (
    <>
      <Stack wrap align="center" gap={2} mb={3}>
        <Heading size={3} mb={0} me="auto">{title}</Heading>
        <ButtonGroup size="sm" aria-label="Calendar view">
          {['month', 'week', 'year'].map((v) => (
            <Button key={v} variant="secondary" outline={period.view !== v} onClick={() => setParams(views[v])} aria-pressed={period.view === v}>
              {v[0].toUpperCase() + v.slice(1)}
            </Button>
          ))}
        </ButtonGroup>
        <ButtonGroup size="sm">
          <Button variant="secondary" outline onClick={() => setParams(prev)} aria-label={`Previous ${period.view}`}>‹</Button>
          <Button variant="secondary" outline onClick={() => setParams(current)}>{thisLabel}</Button>
          <Button variant="secondary" outline onClick={() => setParams(next)} aria-label={`Next ${period.view}`}>›</Button>
        </ButtonGroup>
        <Select size="sm" style={{ width: 'auto' }} aria-label="Which walks" value={show} onChange={(e) => setShow(e.target.value)}>
          <option value="counted">Each walk once</option>
          <option value="all">Every recording</option>
        </Select>
      </Stack>

      {period.view === 'month' && <MonthView y={period.y} m={period.m} byDay={byDay} unit={unit} show={show} title={title} />}
      {period.view === 'week' && <WeekView monday={period.monday} byDay={byDay} unit={unit} show={show} />}
      {period.view === 'year' && <YearView y={period.y} byDay={byDay} unit={unit} show={show} />}
    </>
  )
}

function Summary({ list, unit, show, period }) {
  return (
    <Text as="p" muted text="small" mb={2}>
      {walksLabel(counted(list).length)}, {formatDistance(sumMeters(list), unit, 1)} this {period}.
      {show === 'all' && ' Totals count each walk once; other recordings are greyed.'}
    </Text>
  )
}

function MonthView({ y, m, byDay, unit, show, title }) {
  const weeks = weeksOf(y, m)
  const today = todayKey()
  const monthWalks = weeks.flat().filter((d) => d.getUTCMonth() === m).flatMap((d) => byDay.get(dateKey(d)) ?? [])
  return (
    <>
      <Summary list={monthWalks} unit={unit} show={show} period="month" />
      <div className="calendar" role="grid" aria-label={title}>
        <div className="calendar-row calendar-head" role="row">
          {WEEKDAYS.map((d) => <div key={d} role="columnheader">{d}</div>)}
          <Box role="columnheader" textAlign="end">Week</Box>
        </div>
        {weeks.map((week) => {
          const weekWalks = week.flatMap((d) => byDay.get(dateKey(d)) ?? [])
          return (
            <div key={dateKey(week[0])} className="calendar-row" role="row">
              {week.map((d) => {
                const key = dateKey(d)
                return (
                  <div
                    key={key}
                    role="gridcell"
                    className={`calendar-day${d.getUTCMonth() !== m ? ' calendar-outside' : ''}${key === today ? ' calendar-today' : ''}`}
                  >
                    <div className="calendar-date">{d.getUTCDate()}</div>
                    {(byDay.get(key) ?? []).map((w) => (
                      <TextLink
                        key={w.id}
                        to={`/walks/${w.id}`}
                        className="calendar-walk"
                        muted={!w.is_counted}
                        title={`${w.name} · ${sourceLabel(w)}`}
                      >
                        <span className="calendar-time">{formatTime(w.start_time)} </span>
                        {formatDistance(w.distance_meters, unit, 1)}
                        {w.starred && <StarIcon ms={1} />}
                        <MediaIcons walk={w} />
                      </TextLink>
                    ))}
                  </div>
                )
              })}
              <Box role="gridcell" className="calendar-week" textAlign="end">
                <TextLink to={toSearch(weekParams(week[0]))} inheritColor title="Open this week">
                  {counted(weekWalks).length > 0 ? formatDistance(sumMeters(weekWalks), unit, 1) : '·'}
                </TextLink>
              </Box>
            </div>
          )
        })}
      </div>
    </>
  )
}

// Week: one row per day with each walk's details, like the walks table but grouped by day.
function WeekView({ monday, byDay, unit, show }) {
  const days = [...Array(7)].map((_, i) => addDays(monday, i))
  const weekWalks = days.flatMap((d) => byDay.get(dateKey(d)) ?? [])
  const today = todayKey()
  return (
    <>
      <Summary list={weekWalks} unit={unit} show={show} period="week" />
      <Table responsive middle>
        <thead>
          <tr>
            <th>Day</th><th>Time</th><th>Walk</th><Th hideBelow="md">Recorded with</Th>
            <Th numeric>Distance</Th><Th numeric hideBelow="sm">Moving time</Th>
            <Th numeric hideBelow="sm">Pace</Th><Th numeric hideBelow="md">Climb</Th>
          </tr>
        </thead>
        <tbody>
          {days.map((d) => {
            const key = dateKey(d)
            const list = byDay.get(key) ?? []
            const label = d.toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' })
            const dayCell = (
              <Th scope="row" rowSpan={Math.max(1, list.length)} nowrap weight="semibold" tone={key === today ? 'primary' : undefined}>
                {label}
                {counted(list).length > 1 && <Box text="small" muted weight="normal">{formatDistance(sumMeters(list), unit, 1)}</Box>}
              </Th>
            )
            if (list.length === 0) {
              return <tr key={key}>{dayCell}<Td colSpan={7} muted>–</Td></tr>
            }
            return list.map((w, i) => (
              <Tr key={w.id} muted={!w.is_counted}>
                {i === 0 && dayCell}
                <td>{formatTime(w.start_time)}</td>
                <td>
                  <TextLink to={`/walks/${w.id}`} linkTone={w.is_counted ? undefined : 'muted'}>{w.name}</TextLink>
                  {w.starred && <StarIcon ms={1} />}
                  <MediaIcons walk={w} />
                </td>
                <Td nowrap hideBelow="md">{sourceLabel(w)}</Td>
                <Td numeric nowrap>{formatDistance(w.distance_meters, unit)}</Td>
                <Td numeric hideBelow="sm">{formatDuration(w.moving_duration_ms)}</Td>
                <Td numeric hideBelow="sm">{formatPace(w.moving_duration_ms, w.distance_meters, unit)}</Td>
                <Td numeric nowrap hideBelow="md">{w.elevation_gain_meters == null ? '–' : `${formatNumber(w.elevation_gain_meters)} m`}</Td>
              </Tr>
            ))
          })}
        </tbody>
        <tfoot>
          <Tr weight="semibold">
            <Td colSpan={4} hideBelow="md">Week</Td>
            <Td colSpan={3} hideAbove="md">Week</Td>
            <Td numeric nowrap>{formatDistance(sumMeters(weekWalks), unit, 1)}</Td>
            <Td numeric hideBelow="sm">{formatDuration(counted(weekWalks).reduce((s, w) => s + w.moving_duration_ms, 0))}</Td>
            <Td hideBelow="sm" /><Td hideBelow="md" />
          </Tr>
        </tfoot>
      </Table>
    </>
  )
}

// Year: a small grid per month, each day shaded by its counted distance in four bands (one blue,
// light to dark; dark mode has its own steps; both validated as an ordinal ramp, 2026-10-02), plus
// a table of month totals. Hovering or focusing a day shows its walks in the line above the grids.
const BANDS = { mi: [2, 4, 6], km: [3, 6, 10] } // band edges in the shown unit
function bandOf(meters, unit) {
  if (!meters) return 0
  const v = meters / UNITS[unit].meters
  return 1 + BANDS[unit].filter((edge) => v >= edge).length
}

function YearView({ y, byDay, unit, show }) {
  const [hovered, setHovered] = useState(null)
  const today = todayKey()
  const edges = BANDS[unit]
  const legend = [`under ${edges[0]}`, `${edges[0]}–${edges[1]}`, `${edges[1]}–${edges[2]}`, `${edges[2]}+`]
  const months = [...Array(12)].map((_, m) => {
    const weeks = weeksOf(y, m)
    const list = weeks.flat().filter((d) => d.getUTCMonth() === m).flatMap((d) => byDay.get(dateKey(d)) ?? [])
    return { m, weeks, list }
  })
  const yearList = months.flatMap((x) => x.list)

  const readout = hovered
    ? (() => {
        const list = byDay.get(hovered) ?? []
        const label = utcDate(hovered).toLocaleDateString('en-GB', { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' })
        return list.length
          ? `${label}: ${walksLabel(counted(list).length)}, ${formatDistance(sumMeters(list), unit, 1)}`
          : `${label}: no walks`
      })()
    : 'Hover over or tap a day for its walks; click to open its week.'

  return (
    <>
      <Summary list={yearList} unit={unit} show={show} period="year" />
      <Stack wrap align="center" gap={3} text="small" mb={2}>
        <Text muted aria-live="polite">{readout}</Text>
        <Stack as="span" ms="auto" align="center" gap={2} aria-label="Distance per day">
          {legend.map((text, i) => (
            <Stack as="span" inline key={text} align="center" gap={1}>
              <span className={`year-swatch year-band-${i + 1}`} />{text}
            </Stack>
          ))}
          <span>{UNITS[unit].label}</span>
        </Stack>
      </Stack>
      <Box className="year-grid" mb={4}>
        {months.map(({ m, weeks, list }) => (
          <div key={m}>
            <Stack justify="between" text="small" mb={1}>
              <TextLink to={toSearch(monthParams(y, m))} weight="semibold">
                {new Date(Date.UTC(y, m, 1)).toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'long' })}
              </TextLink>
              <Text muted>{counted(list).length ? formatDistance(sumMeters(list), unit, 0) : ''}</Text>
            </Stack>
            <div className="year-month">
              {WEEKDAYS.map((d) => <span key={d} className="year-head" aria-hidden="true">{d[0]}</span>)}
              {weeks.flat().map((d) => {
                const key = dateKey(d)
                if (d.getUTCMonth() !== m) return <span key={key} />
                const meters = sumMeters(byDay.get(key) ?? [])
                return (
                  <Link
                    key={key}
                    to={toSearch(weekParams(d))}
                    className={`year-day ${key > today ? 'year-future' : `year-band-${bandOf(meters, unit)}`}${key === today ? ' year-today' : ''}`}
                    aria-label={`${key}: ${meters ? formatDistance(meters, unit, 1) : 'no walks'}`}
                    onMouseEnter={() => setHovered(key)}
                    onFocus={() => setHovered(key)}
                    onMouseLeave={() => setHovered(null)}
                  />
                )
              })}
            </div>
          </div>
        ))}
      </Box>
      <Heading level={2} size={6}>Months</Heading>
      <Table autoWidth>
        <thead><tr><th>Month</th><Th numeric>Walks</Th><Th numeric>Distance</Th></tr></thead>
        <tbody>
          {months.map(({ m, list }) => (
            <tr key={m}>
              <td><TextLink to={toSearch(monthParams(y, m))}>{new Date(Date.UTC(y, m, 1)).toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'long' })}</TextLink></td>
              <Td numeric>{counted(list).length || ''}</Td>
              <Td numeric nowrap>{counted(list).length ? formatDistance(sumMeters(list), unit, 1) : ''}</Td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <Tr weight="semibold">
            <td>{y}</td>
            <Td numeric>{counted(yearList).length}</Td>
            <Td numeric nowrap>{formatDistance(sumMeters(yearList), unit, 1)}</Td>
          </Tr>
        </tfoot>
      </Table>
    </>
  )
}
